import { useSelector } from 'react-redux';
import { profileAction } from '@store/profile';
import { ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { getProfile } from '@api/services/console-user.service';
import { CircularProgress } from '@library/loaders/components';
import store, { TRootState } from '@store/index';
import useAuthSelector from 'modules/shared/hooks/use-auth-selector';
import useCurrentUser from 'modules/shared/layouts/hooks/useCurrentUser';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { authActions } from '@store/auth';
import useAppApiUrlData from '@hooks/useAppApiUrlData';
import IllustrationView from '@components/IllustrationView/IllustrationView';
import StopWatchIllustration from '@components/Illustrations/StopWatchIllustration';

interface AuthGuardProps {
    page: ReactNode;
}

const AuthGuard = (props: AuthGuardProps) => {
    const location = useLocation();
    const { token } = useAuthSelector();
    const { info } = useSelector((state: TRootState) => state.profile);

    const { user, isLoading } = useCurrentUser();
    const appApiUrlData = useAppApiUrlData();

    const resellerId = user?.resellerId;

    // ✅ Hook must always run (enabled controls whether it actually fetches)
    const profileQuery = useQuery({
        queryKey: ['profile', resellerId],
        queryFn: () => getProfile(),
        enabled: Boolean(resellerId) && Boolean(token) && !appApiUrlData.isLoading && !appApiUrlData.isError,
    });

    // ✅ Hook must always run
    useEffect(() => {
        if (profileQuery.data) {
            store.dispatch(profileAction.setProfile(profileQuery.data));
        }
    }, [profileQuery.data]);

    // Now it’s safe to early-return UI states
    // if (appApiUrlData.isLoading) return <CircularProgress />;

    if (appApiUrlData.isError) {
        return (
            <IllustrationView
                className="mt-[25vh]"
                illustration={<StopWatchIllustration />}
                title="Page Not Found"
                message={
                    <>
                        Sorry but the page you are looking for does not exist, <br /> have been removed or renamed.
                    </>
                }
            />
        );
    }

    if (!token) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (isLoading || !info || appApiUrlData.isLoading) {
        return (
            <div className="h-screen w-screen flex justify-center items-center">
                <CircularProgress />
            </div>
        );
    }

    const payload = jwtDecode(token) as any;
    const isExpired = moment().isAfter(moment.unix(payload.exp));

    if (isExpired) {
        store.dispatch(authActions.logout());
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return props.page;
};

export default AuthGuard;
